import { useTimezone } from '@contexts/useTimezone';
import { PilotOnboardingService } from '@services/PilotOnboardingService';
import { HollowedButton, NormalButton } from '@shared/designSystem/Button';
import { ColumnsContainer, HorizontalContainer, VerticalContainer } from '@shared/designSystem/Containers';
import DetailField from '@shared/designSystem/DetailField';
import { TextInput } from '@shared/designSystem/TextInput';
import { Modal } from '@shared/designSystem/messages/Modal';
import { ToastManager } from '@shared/designSystem/messages/ToastManager';
import { Text } from '@shared/designSystem/Texts';
import designSystemStyles from '@shared/styles/designSystemStyles';
import { Description } from '@shared/utils/DescriptionsHelper';
import { isValidPilotCountryCode, isValidPilotTimezone, usePilotOnboardingDetailModalViewModel } from './PilotOnboardingDetailModalViewModel';
import { getPilotSourceLabel } from './PilotSourceOptions';
import { PilotCatalogTableRow } from './PilotOnboardingTableViewModel';

interface PilotOnboardingDetailModalProps {
    pilot: PilotCatalogTableRow | null;
    isOpen: boolean;
    pilotOnboardingService: PilotOnboardingService;
    onClose: () => void;
    onSaved: () => void | Promise<void>;
}

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={`**${title.trim()}**`} color={designSystemStyles.colorText} markdown={true} />
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            {children}
        </VerticalContainer>
    </VerticalContainer>
);

const PilotOnboardingDetailModal: React.FC<PilotOnboardingDetailModalProps> = ({
    pilot,
    isOpen,
    pilotOnboardingService,
    onClose,
    onSaved,
}) => {
    const { timezone } = useTimezone();
    const viewModel = usePilotOnboardingDetailModalViewModel({
        pilot,
        pilotOnboardingService,
    });

    const handleSave = async () => {
        const result = await viewModel.save();

        if (result.kind === 'success') {
            ToastManager.shared().showSuccessMessage(result.message);
            onClose();
            await onSaved();
            return;
        }

        ToastManager.shared().showErrorMessage(result.message);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={pilot?.name ?? "Detalle del piloto".localized()}
            width="880px"
            height="75vh"
            maxWidth="94vw"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                {pilot ? (
                    <>
                        <div style={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                            <VerticalContainer
                                width="100%"
                                gap={designSystemStyles.gapLg}
                                paddingBottom={designSystemStyles.paddingSm}
                            >
                                <DetailSection title={"Resumen".localized()}>
                                    <ColumnsContainer columns={2} gap={designSystemStyles.gapLg}>
                                        <DetailField label={"ID".localized()} value={pilot.id} />
                                        <TextInput
                                            label={"Nombre".localized()}
                                            value={viewModel.name}
                                            onChange={viewModel.setName}
                                            placeholder={"Nombre de la organizacion".localized()}
                                            error={viewModel.name.trim() ? undefined : "El nombre no puede estar vacio".localized()}
                                            disabled={viewModel.isSaving}
                                        />
                                        <TextInput
                                            label={"Pais".localized()}
                                            value={viewModel.countryCode}
                                            onChange={(value) => viewModel.setCountryCode(value.toUpperCase().slice(0, 2))}
                                            placeholder="AR"
                                            error={!viewModel.countryCode || isValidPilotCountryCode(viewModel.countryCode)
                                                ? undefined
                                                : "Codigo de pais de 2 letras".localized()}
                                            disabled={viewModel.isSaving}
                                        />
                                        <TextInput
                                            label={"Timezone".localized()}
                                            value={viewModel.timezone}
                                            onChange={viewModel.setTimezone}
                                            placeholder="America/Argentina/Buenos_Aires"
                                            error={!viewModel.timezone || isValidPilotTimezone(viewModel.timezone)
                                                ? undefined
                                                : "Timezone invalida (formato IANA)".localized()}
                                            disabled={viewModel.isSaving}
                                        />
                                        <DetailField
                                            label={"Creado".localized()}
                                            value={pilot.creationTimestamp > 0
                                                ? Description.Timestamp.date(pilot.creationTimestamp, timezone)
                                                : null}
                                        />
                                        <DetailField
                                            label={"Ultima actividad".localized()}
                                            value={pilot.lastTimeUpdatedTimestamp != null
                                                ? Description.Timestamp.date(pilot.lastTimeUpdatedTimestamp, timezone)
                                                : null}
                                        />
                                        <DetailField
                                            label={"Origen".localized()}
                                            value={getPilotSourceLabel(pilot.pilotSource) ?? pilot.pilotSource}
                                        />
                                        <DetailField label={"Usuarios limite".localized()} value={pilot.usersLimit} />
                                        <DetailField label={"Sin actividad > 7 dias".localized()} value={pilot.isIDHighlighted ? "Si".localized() : "No".localized()} />
                                    </ColumnsContainer>
                                </DetailSection>

                                <DetailSection title={"Usuarios".localized()}>
                                    <ColumnsContainer columns={3} gap={designSystemStyles.gapLg}>
                                        <DetailField label={"Ultimas 2 semanas".localized()} value={pilot.totalUsers} />
                                        <DetailField label={"Ultima semana".localized()} value={pilot.usersLastWeek} />
                                        <DetailField label={"Semana previa".localized()} value={pilot.usersPreviousWeek} />
                                    </ColumnsContainer>
                                </DetailSection>

                                <DetailSection title={"Tickets".localized()}>
                                    <ColumnsContainer columns={3} gap={designSystemStyles.gapLg}>
                                        <DetailField label={"Ultimas 2 semanas".localized()} value={pilot.ticketsLast2Weeks} />
                                        <DetailField label={"Ultima semana".localized()} value={pilot.ticketsLastWeek} />
                                        <DetailField label={"Semana previa".localized()} value={pilot.ticketsPreviousWeek} />
                                    </ColumnsContainer>
                                </DetailSection>
                            </VerticalContainer>
                        </div>

                        <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapMd}>
                            <HollowedButton
                                text={"Cerrar".localized()}
                                onClick={onClose}
                                disabled={viewModel.isSaving}
                            />
                            <NormalButton
                                text={"Guardar cambios".localized()}
                                onClick={() => void handleSave()}
                                loading={viewModel.isSaving}
                                disabled={!viewModel.canSave}
                                width="200px"
                            />
                        </HorizontalContainer>
                    </>
                ) : null}
            </VerticalContainer>
        </Modal>
    );
};

export default PilotOnboardingDetailModal;
